/**
 * Web Bluetooth driver for the Felicita Arc, Parallel and Incline. EXPERIMENTAL: written from other projects'
 * reverse-engineered notes, never tried with Taretto on real hardware. Wire format lives in felicitaProtocol.ts; see
 * docs/felicita-protocol.md.
 *
 * Lifecycle: the registry (registry.ts) creates a FelicitaScale for a device the picker or Chrome's getDevices()
 * returned. connect() opens GATT, checks for the FFE0 service and subscribes to FFE1, which starts the ~10 Hz weight
 * stream. No handshake, no heartbeat. Later calls (the store's auto-reconnect, "Reconnect") reuse the same
 * BluetoothDevice and only re-open GATT.
 *
 * FFE0 is a generic UART service that many devices use, so the registry never picks this driver by its service alone
 * (see registry.ts). The service check here only rules out devices without FFE0.
 *
 * Writes go through one queue, at least MIN_WRITE_GAP_MS apart (Beanconqueror gives the Arc an 80 ms command delay).
 * Commands are one ASCII byte each, and the scale confirms none of them.
 *
 * Liveness watchdog: no frame for STALE_MS reports onStale(true); a much longer silence while the page is visible
 * drops the link so the store's reconnect path runs.
 *
 * Optional capabilities (types.ts): diagnostics (the packet log summary). No powerOff (no source documents one), no
 * keepAwake and no tareCheck.
 */
import { connectTimeout, describeBleError, getDriverService, pickWriteMode, ScaleServiceMissingError } from './bluetooth';
import {
  FELICITA_CHAR_UUID,
  FELICITA_NAME_PREFIX,
  FELICITA_SERVICE_UUID,
  felicitaCommands,
  isFelicitaName,
  parseFelicitaFrame,
  type FelicitaUnit,
} from './felicitaProtocol';
import type { BluetoothScaleDriverDescriptor, RawPacketListener, RawWriteListener, ScaleConnectOptions, ScaleDiagnosticsCapability, ScaleDiagnosticsItem, ScaleDriver, ScaleReading } from './types';

const CONNECT_TIMEOUT_MS = 15_000;
const CONNECT_RETRY_DELAY_MS = 600;
const WRITE_RETRY_DELAY_MS = 150;
/** Beanconqueror uses 80 ms between commands for the Arc; the Bookoo driver's 100 ms covers it. */
export const MIN_WRITE_GAP_MS = 100;

const WATCHDOG_CHECK_MS = 500;
/** The scale streams at about 10 Hz: no frame for this long means the last weight is no longer live. */
export const STALE_MS = 1_500;
/** Silence, counted only while the page is visible, after which the link is dropped to force a reconnect. */
export const STALE_DISCONNECT_MS = 12_000;

/** Registry entry (registry.ts). FFE0 is shared with countless UART gadgets, so the picker filters by name only. */
export const felicitaDriver: BluetoothScaleDriverDescriptor = {
  kind: 'felicita',
  transport: 'bluetooth',
  label: 'Felicita Arc / Parallel / Incline',
  shortName: 'Felicita',
  filters: [{ namePrefix: FELICITA_NAME_PREFIX }],
  bluefyFilter: { namePrefix: FELICITA_NAME_PREFIX },
  optionalServices: [FELICITA_SERVICE_UUID],
  primaryServices: [FELICITA_SERVICE_UUID],
  matches: isFelicitaName,
  create: (device) => new FelicitaScale(device),
  capabilities: ['diagnostics'],
  experimental: true,
  copy: {
    pickerName: 'FELICITA',
    vendorApp: 'the Felicita Coffee app',
    controlsNote: 'The Felicita doesn’t report its timer to Taretto: the timer buttons only drive the scale’s own display.',
  },
};

export interface FelicitaDiagnostics {
  /** Notifications received. */
  packets: number;
  weightPackets: number;
  /** Shorter than 9 bytes, or a weight field that isn't six digits. Dropped. */
  malformed: number;
  /** Used, but not the usual 18 bytes. */
  oddLength: number;
  /** Used, but bytes 0-2 aren't 01 02 and a sign. */
  oddHeader: number;
  unit: FelicitaUnit | null;
  batteryRaw: number | null;
  writeMode: 'withResponse' | 'withoutResponse' | 'legacy' | null;
  stale: boolean;
}

interface Session {
  server: BluetoothRemoteGATTServer;
  char: BluetoothRemoteGATTCharacteristic;
  timers: Array<ReturnType<typeof setInterval>>;
  cleanup: Array<() => void>;
  lastFrameAt: number;
  /** Last time the page became visible again or the watchdog timer itself was frozen. */
  resumeAt: number;
  lastCheckAt: number;
  stale: boolean;
}

export class FelicitaScale implements ScaleDriver {
  readonly kind = 'felicita' as const;

  private readonly device: BluetoothDevice;
  private session: Session | null = null;
  private connecting: Promise<void> | null = null;
  /** Incremented to invalidate an in-flight connection attempt (timeout / disconnect()). */
  private attempt = 0;
  private writeChain: Promise<void> = Promise.resolve();
  private lastWriteAt = Number.NEGATIVE_INFINITY;
  private readonly readingListeners = new Set<(r: ScaleReading) => void>();
  private readonly disconnectListeners = new Set<(reason?: string) => void>();
  private readonly rawListeners = new Set<RawPacketListener>();
  private readonly writeListeners = new Set<RawWriteListener>();
  private readonly staleListeners = new Set<(stale: boolean) => void>();
  private diag: FelicitaDiagnostics = emptyDiagnostics();

  readonly diagnostics: ScaleDiagnosticsCapability = {
    summary: () => ({ badge: 'Felicita', items: diagnosticsItems(this.stats) }),
  };

  constructor(device: BluetoothDevice) {
    this.device = device;
  }

  get name(): string {
    return this.device.name?.trim() || 'Felicita scale';
  }

  get connected(): boolean {
    return this.session?.server.connected === true;
  }

  get stats(): FelicitaDiagnostics {
    return { ...this.diag };
  }

  connect(options: ScaleConnectOptions = {}): Promise<void> {
    if (this.connected) return Promise.resolve();
    if (this.connecting) return this.connecting;
    this.endSession();
    const attempt = ++this.attempt;
    const p = this.establish(attempt, options.timeoutMs ?? CONNECT_TIMEOUT_MS).finally(() => {
      if (this.connecting === p) this.connecting = null;
    });
    this.connecting = p;
    return p;
  }

  /** Closes the session, or cancels (and aborts) a pending connection attempt. Safe to call repeatedly. */
  async disconnect(): Promise<void> {
    this.attempt++;
    const hadSession = this.session !== null;
    const active = hadSession || this.connecting !== null;
    this.connecting = null;
    this.endSession();
    this.device.removeEventListener('gattserverdisconnected', this.onGattDisconnected);
    if (active) this.device.gatt?.disconnect();
    if (hadSession) this.emitDisconnect();
  }

  /** Not confirmed by the scale: the engine sees the tare land in the weight stream. */
  tare(): Promise<void> {
    return this.send('tare', felicitaCommands.tare());
  }

  startTimer(): Promise<void> {
    return this.send('start timer', felicitaCommands.startTimer());
  }

  stopTimer(): Promise<void> {
    return this.send('stop timer', felicitaCommands.stopTimer());
  }

  resetTimer(): Promise<void> {
    return this.send('reset timer', felicitaCommands.resetTimer());
  }

  onReading(cb: (r: ScaleReading) => void): () => void {
    return subscribe(this.readingListeners, cb);
  }

  onDisconnect(cb: (reason?: string) => void): () => void {
    return subscribe(this.disconnectListeners, cb);
  }

  /** Every notification as received, before parsing. */
  onRawPacket(cb: RawPacketListener): () => void {
    return subscribe(this.rawListeners, cb);
  }

  /** Every write attempt to the scale, with whether it went out. */
  onRawWrite(cb: RawWriteListener): () => void {
    return subscribe(this.writeListeners, cb);
  }

  onStale(cb: (stale: boolean) => void): () => void {
    return subscribe(this.staleListeners, cb);
  }

  // ---------------------------------------------------------------- connection

  private async establish(attempt: number, timeoutMs: number): Promise<void> {
    const device = this.device;
    device.addEventListener('gattserverdisconnected', this.onGattDisconnected);
    const timeout = connectTimeout(timeoutMs);
    try {
      await Promise.race([this.openSessionWithRetry(device, attempt), timeout.promise]);
    } catch (e) {
      if (attempt === this.attempt) {
        this.attempt++;
        this.endSession();
        device.gatt?.disconnect();
      }
      throw describeBleError(e, felicitaDriver);
    } finally {
      timeout.clear();
    }
  }

  private async openSessionWithRetry(device: BluetoothDevice, attempt: number): Promise<void> {
    try {
      await this.openSession(device, attempt);
    } catch (e) {
      // Android's BLE stack regularly fails the first GATT connect "for unknown reason" (decaid saw GATT status 133
      // on a Felicita); one retry fixes most.
      const name = e instanceof Error ? e.name : '';
      if (attempt !== this.attempt || e instanceof ScaleServiceMissingError || name === 'NotFoundError' || name === 'SecurityError') throw e;
      await delay(CONNECT_RETRY_DELAY_MS);
      this.assertCurrent(attempt);
      await this.openSession(device, attempt);
    }
  }

  private async openSession(device: BluetoothDevice, attempt: number): Promise<void> {
    const gatt = device.gatt;
    if (!gatt) throw new Error('This Bluetooth device has no GATT server.');
    const server = gatt.connected ? gatt : await gatt.connect();
    this.assertCurrent(attempt);
    // The service check: FFE0 is generic, so this only rules out devices that don't have it at all.
    const service = await getDriverService(server, felicitaDriver);
    this.assertCurrent(attempt);
    const char = await service.getCharacteristic(FELICITA_CHAR_UUID);
    this.assertCurrent(attempt);

    const cleanup: Array<() => void> = [];
    // A listener per attempt: a superseded attempt's cleanup must not remove a newer session's listener.
    const onValue = (ev: Event) => this.onNotification(ev, char);
    char.addEventListener('characteristicvaluechanged', onValue);
    cleanup.push(() => char.removeEventListener('characteristicvaluechanged', onValue));
    try {
      await char.startNotifications();
      this.assertCurrent(attempt);
    } catch (e) {
      cleanup.forEach((fn) => fn());
      throw e;
    }

    const now = performance.now();
    this.diag = { ...emptyDiagnostics(), writeMode: pickWriteMode(char) };
    const session: Session = { server, char, timers: [], cleanup, lastFrameAt: now, resumeAt: now, lastCheckAt: now, stale: false };
    this.session = session;
    this.startWatchdog(session);
  }

  private assertCurrent(attempt: number): void {
    if (attempt !== this.attempt) throw new Error('Connection attempt was cancelled.');
  }

  private endSession(): void {
    const s = this.session;
    if (!s) return;
    this.session = null;
    s.timers.forEach((t) => clearInterval(t));
    s.cleanup.forEach((fn) => fn());
  }

  /** Only reported for established sessions: failures while connecting reject connect() instead. */
  private readonly onGattDisconnected = (): void => {
    if (!this.session) return;
    this.endSession();
    this.emitDisconnect('Lost connection to the scale.');
  };

  private emitDisconnect(reason?: string): void {
    for (const cb of [...this.disconnectListeners]) safeCall(() => cb(reason));
  }

  // ---------------------------------------------------------------- liveness watchdog

  private startWatchdog(s: Session): void {
    s.timers.push(setInterval(() => this.checkLiveness(s), WATCHDOG_CHECK_MS));
    if (typeof document === 'undefined' || typeof document.addEventListener !== 'function') return;
    // A page that was hidden (and possibly frozen) restarts the silence clock instead of blaming the scale.
    const onVisibility = () => {
      if (document.visibilityState === 'hidden') return;
      const now = performance.now();
      s.resumeAt = now;
      s.lastCheckAt = now;
    };
    document.addEventListener('visibilitychange', onVisibility);
    s.cleanup.push(() => document.removeEventListener('visibilitychange', onVisibility));
  }

  private checkLiveness(s: Session): void {
    if (this.session !== s) return;
    const now = performance.now();
    const sinceLastCheck = now - s.lastCheckAt;
    s.lastCheckAt = now;
    if (!pageVisible()) return;
    if (sinceLastCheck > WATCHDOG_CHECK_MS * 4) {
      // The timer itself was frozen or throttled (background tab, device sleep): our clock is unreliable.
      s.resumeAt = now;
      return;
    }
    const silentMs = now - Math.max(s.lastFrameAt, s.resumeAt);
    if (silentMs >= STALE_DISCONNECT_MS) {
      this.forceDisconnect('The scale stopped sending data.');
      return;
    }
    if (!s.stale && silentMs >= STALE_MS) {
      s.stale = true;
      this.diag.stale = true;
      this.emitStale(true);
    }
  }

  private noteAlive(s: Session, t: number): void {
    s.lastFrameAt = t;
    if (s.stale) {
      s.stale = false;
      this.diag.stale = false;
      this.emitStale(false);
    }
  }

  private forceDisconnect(reason: string): void {
    if (!this.session) return;
    this.endSession();
    this.device.gatt?.disconnect();
    this.emitDisconnect(reason);
  }

  private emitStale(stale: boolean): void {
    for (const cb of [...this.staleListeners]) safeCall(() => cb(stale));
  }

  // ---------------------------------------------------------------- incoming data

  private onNotification(ev: Event, ch: BluetoothRemoteGATTCharacteristic): void {
    // Bluefy dispatches plain objects rather than real Events; fall back to the characteristic itself.
    const value = (ev?.target as BluetoothRemoteGATTCharacteristic | null | undefined)?.value ?? ch.value;
    if (!value) return;
    const t = performance.now();
    if (this.session) this.noteAlive(this.session, t);
    // Copy: Chrome may reuse the underlying buffer, and raw listeners may keep the bytes.
    const bytes = new Uint8Array(value.byteLength);
    bytes.set(new Uint8Array(value.buffer, value.byteOffset, value.byteLength));
    const d = this.diag;
    d.packets++;
    for (const cb of [...this.rawListeners]) safeCall(() => cb(bytes, t));

    const result = parseFelicitaFrame(bytes);
    if (!result.ok) {
      d.malformed++;
      return;
    }
    const frame = result.frame;
    d.weightPackets++;
    if (!frame.lengthOk) d.oddLength++;
    if (!frame.headerOk) d.oddHeader++;
    d.unit = frame.unit;
    if (frame.batteryRaw !== null) d.batteryRaw = frame.batteryRaw;
    const reading: ScaleReading = { t, weightG: frame.weightG, flowGs: null, timerMs: null, batteryPct: frame.batteryPct };
    for (const cb of [...this.readingListeners]) safeCall(() => cb(reading));
  }

  // ---------------------------------------------------------------- commands

  /** Queue a command behind any in-flight write, at least MIN_WRITE_GAP_MS after the previous one. */
  private send(label: string, frame: Uint8Array<ArrayBuffer>): Promise<void> {
    if (!this.connected) return Promise.reject(commandError(label, new Error('Scale not connected')));
    const run = () => this.writeSpaced(label, frame);
    const result = this.writeChain.then(run, run);
    this.writeChain = result.catch(() => undefined);
    return result;
  }

  private async writeSpaced(label: string, frame: Uint8Array<ArrayBuffer>): Promise<void> {
    const wait = this.lastWriteAt + MIN_WRITE_GAP_MS - performance.now();
    if (wait > 0) await delay(wait);
    if (!this.connected) throw commandError(label, new Error('Scale not connected'));
    try {
      await this.writeWithRetry(label, frame);
    } finally {
      this.lastWriteAt = performance.now();
    }
  }

  private async writeWithRetry(label: string, frame: Uint8Array<ArrayBuffer>): Promise<void> {
    try {
      await this.writeAttempt(label, frame);
    } catch (first) {
      if (!this.connected) throw commandError(label, first);
      await delay(WRITE_RETRY_DELAY_MS);
      try {
        await this.writeAttempt(label, frame);
      } catch (second) {
        throw commandError(label, second);
      }
    }
  }

  /** One write, reported to the onRawWrite listeners whether it went out or not. */
  private async writeAttempt(label: string, frame: Uint8Array<ArrayBuffer>): Promise<void> {
    let ok = false;
    try {
      await this.writeOnce(frame);
      ok = true;
    } finally {
      const t = performance.now();
      for (const cb of [...this.writeListeners]) safeCall(() => cb(frame, label, t, ok));
    }
  }

  private async writeOnce(frame: Uint8Array<ArrayBuffer>): Promise<void> {
    const s = this.session;
    if (!s || !s.server.connected) throw new Error('Scale not connected');
    const ch = s.char;
    // A fresh one-byte buffer: Bluefy has been reported to send a view's whole underlying buffer, and every extra
    // byte would reach the scale as another command.
    const bytes = frame.slice();
    switch (pickWriteMode(ch)) {
      case 'withResponse':
        return ch.writeValueWithResponse(bytes);
      case 'withoutResponse':
        return ch.writeValueWithoutResponse(bytes);
      case 'legacy':
        return ch.writeValue(bytes);
    }
  }
}

// ---------------------------------------------------------------- helpers

function emptyDiagnostics(): FelicitaDiagnostics {
  return { packets: 0, weightPackets: 0, malformed: 0, oddLength: 0, oddHeader: 0, unit: null, batteryRaw: null, writeMode: null, stale: false };
}

/** The packet log's diagnostics line. */
function diagnosticsItems(d: FelicitaDiagnostics): ScaleDiagnosticsItem[] {
  const items: ScaleDiagnosticsItem[] = [
    { text: `${d.packets} frames` },
    { text: `${d.weightPackets} weight` },
    { text: `${d.malformed} malformed (dropped)`, tone: d.malformed > 0 ? 'warning' : undefined },
  ];
  if (d.oddLength > 0) items.push({ text: `${d.oddLength} not 18 bytes (still used)`, tone: 'warning' });
  if (d.oddHeader > 0) items.push({ text: `${d.oddHeader} unexpected header (still used)`, tone: 'warning' });
  if (d.unit === 'oz') items.push({ text: 'display in ounces, converted to grams (unverified)', tone: 'warning' });
  else if (d.unit === 'unknown') items.push({ text: 'unit not recognised, read as grams', tone: 'warning' });
  if (d.batteryRaw !== null) items.push({ text: `battery raw ${d.batteryRaw}` });
  if (d.writeMode) items.push({ text: `writes: ${d.writeMode}` });
  if (d.stale) items.push({ text: 'no data (stale)', tone: 'danger' });
  return items;
}

function pageVisible(): boolean {
  return typeof document === 'undefined' || document.visibilityState !== 'hidden';
}

function commandError(label: string, cause: unknown): Error {
  const msg = cause instanceof Error ? cause.message : String(cause);
  return new Error(`Couldn't send "${label}" to the scale: ${msg}`, { cause });
}

function subscribe<T>(set: Set<T>, cb: T): () => void {
  set.add(cb);
  return () => {
    set.delete(cb);
  };
}

function safeCall(fn: () => void): void {
  try {
    fn();
  } catch (e) {
    console.error('Felicita scale listener failed', e);
  }
}

function delay(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}
